import { useMemo, useState } from 'react'
import { HOSTING_WINDOWS, findWindow, type WindowId } from '@simplelaunch/shared'
import { ThemePicker } from './components/ThemePicker.js'
import { Preview } from './components/Preview.js'
import { Expired } from './components/Expired.js'
import { Legal } from './components/Legal.js'
import { Landing } from './components/Landing.js'
import { findLegalPage } from './legal.js'
import { readEntry } from './entry.js'
import { ApiError, problemMessage, saveDraft, startCheckout } from './api.js'
import {
  NAME_MAX,
  TAGLINE_MAX,
  type Draft,
  draftToSite,
  effectiveSlug,
  emptyDraft,
  isoDate,
  issueFor,
  launchInstant,
  localTimeZone,
  projectedExpiry,
  validateDraft,
} from './draft.js'

const money = (cents: number) => `$${(cents / 100).toFixed(0)}`

export function App() {
  // Frozen at mount: a `now` that advances on every render would make the
  // launch-date validation flicker and re-run the whole preview.
  const [now] = useState(() => new Date())
  const [timeZone] = useState(localTimeZone)
  const [entry] = useState(() => readEntry(new URL(window.location.href)))
  const [draft, setDraft] = useState<Draft>(() => {
    const blank = emptyDraft(now)
    // Someone who typed a subdomain that nobody owns already told us the name
    // they want; carrying it into the form is the whole point of the redirect.
    const chosen =
      entry.kind === 'build' && entry.windowId
        ? { ...blank, windowId: entry.windowId as Draft['windowId'] }
        : blank
    if (entry.kind === 'build' && entry.claim) {
      return { ...chosen, name: entry.claim, slugTouched: true, slugOverride: entry.claim }
    }
    return chosen
  })

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const slug = effectiveSlug(draft)
  const issues = useMemo(() => validateDraft(draft, now, timeZone), [draft, now, timeZone])
  const site = useMemo(() => draftToSite(draft, timeZone, now), [draft, timeZone, now])
  const expiry = projectedExpiry(draft, now)
  // Not `window`: that shadows the global, and the shadowing is invisible
  // until something reaches for window.location.
  const hostingWindow = findWindow(draft.windowId)
  const ready = issues.length === 0 && draft.name.trim().length > 0

  const [launching, setLaunching] = useState(false)
  const [serverError, setServerError] = useState<string | undefined>()
  const [serverProblems, setServerProblems] = useState<Record<string, string>>({})

  const launch = async () => {
    setLaunching(true)
    setServerError(undefined)
    setServerProblems({})
    try {
      const instant = launchInstant(draft, timeZone)
      const { siteId } = await saveDraft({
        name: draft.name.trim(),
        tagline: draft.tagline.trim(),
        slug,
        launchAt: (instant ?? now).toISOString(),
        timeZone,
        windowId: draft.windowId,
        theme: draft.theme,
        waitlist: draft.waitlist,
      })
      const { url } = await startCheckout(siteId, draft.windowId)
      // Full navigation, not fetch: Stripe Checkout is a hosted page.
      window.location.assign(url)
    } catch (error) {
      setLaunching(false)
      if (error instanceof ApiError) {
        if (error.problems.length > 0) {
          setServerProblems(
            Object.fromEntries(error.problems.map((p) => [p.field, problemMessage(p)])),
          )
          return
        }
        setServerError(error.message)
        return
      }
      setServerError('Could not reach the server. Check your connection and try again.')
    }
  }

  const expiryLabel = new Intl.DateTimeFormat('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', timeZone,
  }).format(expiry)

  if (entry.kind === 'landing') return <Landing />

  if (entry.kind === 'legal') {
    const page = findLegalPage(entry.slug)
    if (page) return <Legal page={page} />
  }

  if (entry.kind === 'expired') return <Expired slug={entry.slug} />

  return (
    <div className="shell">
      <div className="panel">
        <div className="brand">
          <a href="/"><b>simplelaunch<span>.</span></b></a>
        </div>
        <p className="lede">
          A launch page for your startup, live in two minutes. Three fields and a look —
          then ship it.
        </p>

        <fieldset>
          <legend>Your startup</legend>

          <label>
            <span className="lbl">
              Name
              <span className="count" data-over={draft.name.length > NAME_MAX}>
                {draft.name.length}/{NAME_MAX}
              </span>
            </span>
            <input
              type="text"
              value={draft.name}
              maxLength={NAME_MAX + 10}
              placeholder="Northwind"
              autoFocus
              aria-invalid={Boolean((issueFor(issues, 'name') ?? serverProblems['name']))}
              onChange={(e) => set('name', e.target.value)}
            />
            {(issueFor(issues, 'name') ?? serverProblems['name']) && <p className="err">{(issueFor(issues, 'name') ?? serverProblems['name'])}</p>}
          </label>

          <label>
            <span className="lbl">
              One line about it
              <span className="count" data-over={draft.tagline.length > TAGLINE_MAX}>
                {draft.tagline.length}/{TAGLINE_MAX}
              </span>
            </span>
            <textarea
              value={draft.tagline}
              maxLength={TAGLINE_MAX + 20}
              placeholder="Freight forwarding that actually tells you where your container is."
              aria-invalid={Boolean((issueFor(issues, 'tagline') ?? serverProblems['tagline']))}
              onChange={(e) => set('tagline', e.target.value)}
            />
            {(issueFor(issues, 'tagline') ?? serverProblems['tagline']) && <p className="err">{(issueFor(issues, 'tagline') ?? serverProblems['tagline'])}</p>}
          </label>

          <label>
            <span className="lbl">Address</span>
            <input
              type="text"
              value={slug}
              placeholder="northwind"
              aria-invalid={Boolean((issueFor(issues, 'slug') ?? serverProblems['slug']))}
              onChange={(e) => setDraft((d) => ({
                ...d, slugTouched: true, slugOverride: e.target.value.toLowerCase(),
              }))}
            />
            {(issueFor(issues, 'slug') ?? serverProblems['slug'])
              ? <p className="err">{(issueFor(issues, 'slug') ?? serverProblems['slug'])}</p>
              : <p className="hint"><code>{slug || 'your-startup'}.simplelaunch.io</code></p>}
          </label>
        </fieldset>

        <fieldset>
          <legend>Launch date</legend>
          <label>
            <input
              type="date"
              value={draft.launchDate}
              min={isoDate(now, 1)}
              // Capping the picker turns the guardrail into a nudge: the
              // founder discovers the limit by reaching it, not by being
              // rejected after filling everything in.
              max={isoDate(expiry)}
              aria-invalid={Boolean((issueFor(issues, 'launchDate') ?? serverProblems['launchDate']))}
              onChange={(e) => set('launchDate', e.target.value)}
            />
            {(issueFor(issues, 'launchDate') ?? serverProblems['launchDate'])
              ? <p className="err">{(issueFor(issues, 'launchDate') ?? serverProblems['launchDate'])}</p>
              : <p className="hint">Counts down in your visitors' local time.</p>}
          </label>
        </fieldset>

        <fieldset>
          <legend>Keep it live for</legend>
          <div className="windows" role="group" aria-label="Hosting window">
            {HOSTING_WINDOWS.map((w) => (
              <button
                key={w.id}
                type="button"
                className="win"
                aria-pressed={draft.windowId === w.id}
                onClick={() => set('windowId', w.id as WindowId)}
              >
                <b>{money(w.priceCents)}</b>
                <span>{w.label}</span>
              </button>
            ))}
          </div>
          <p className="hint">
            Live until {expiryLabel}. Launching later? You can extend any time.
          </p>
        </fieldset>

        <ThemePicker
          value={draft.theme}
          seed={slug || 'preview'}
          onChange={(theme) => set('theme', theme)}
        />

        <div className="check">
          <input
            id="wl"
            type="checkbox"
            checked={draft.waitlist}
            onChange={(e) => set('waitlist', e.target.checked)}
          />
          <div>
            <label htmlFor="wl" style={{ margin: 0 }}>Collect emails</label>
            <p>Visitors can leave an address. Export the list any time.</p>
          </div>
        </div>

        <button className="cta" type="button" disabled={!ready || launching} onClick={launch}>
          {launching
            ? 'Opening checkout…'
            : ready
              ? `Launch it — ${money(hostingWindow?.priceCents ?? 0)}`
              : 'Launch it'}
        </button>
        {serverError && <p className="err" style={{ textAlign: 'center' }}>{serverError}</p>}
        <p className="cta-note">
          {ready ? 'Next: payment, then your page goes live.' : 'Add a name to continue.'}
        </p>

        <p className="panel-legal">
          <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a> ·{' '}
          <a href="/refunds">Refunds</a> · <a href="/acceptable-use">Acceptable use</a>
        </p>
      </div>

      <div className="stage">
        <div style={{ width: '100%', maxWidth: '60rem' }}>
          <Preview site={site} />
          <p className="stage-note">
            Live preview — this is the exact page that gets published.
          </p>
        </div>
      </div>
    </div>
  )
}
